import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router'
import { EmptyState } from '@/components/common/States'
import { AppShell } from '@/components/layout/AppShell'
import { NAV } from '@/components/layout/nav'
import { DepartmentsPage, PositionsPage, RoleDetailPage, RolesPage, UsersPage } from '@/features/access'
import { AuditPage, CustomerCategoriesPage, DiscountLimitsPage, InfraPage, InfraServerDetailPage, MailAccountsPage, PipelinesPage, RegionsPage } from '@/features/admin'
import { AccountPage } from '@/features/account/AccountPage'
import { TaskBoardPage } from '@/features/projects/boards/TaskBoardPage'
import { TaskRulesPage } from '@/features/projects/rules/TaskRulesPage'
import { MyDocumentsPage } from '@/features/account/MyDocumentsPage'
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage'
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage'
import { LoginPage } from '@/features/auth/LoginPage'
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage'
import { AccessRequestsPage, CollectionsPage, CrmMeetingsPage, CrmOverviewPage, CustomerDetailPage, CustomersPage, CrmFollowupsPage, CrmOffersPage, CrmOrdersPage, CrmRenewalsPage, CustomerExpensesPage, DeliveryPage, MeetingLivePage, SupportPage } from '@/features/crm'
import { DashboardEditPage } from '@/features/dashboard/DashboardEditPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { DemosPage } from '@/features/demos/DemosPage'
import { BankReportPage, BanksPage, CashFlowPage, DepositsPage, FxPage, CategoriesPage, EDocumentsPage, FinanceAccountsPage, FinanceCashAdvancesPage, FinanceLoansPage, FinanceOverviewPage, ObligationsPage, PartiesPage, PaymentRequestsPage, PersonalFinancePage, TaxPage, TransactionsPage, VoidRequestsPage, LedgerBooksPage, PnLPage, ReimbursementsPage, StaffAdvancesPage, BudgetsPage, DepartmentsPage as FinanceDepartmentsPage } from '@/features/finance'
import {
  BelgeTakipPage,
  BelgelerListPage,
  SablonDuzenleyiciPage,
  SablonOlusturPage,
  SablonSurumPage,
} from '@/features/hr/belgeler'
import {
  BordroPage,
  OdemelerPage,
  PersonelCreatePage,
  PersonelDetailPage,
  PersonelEditPage,
  PersonelListPage,
} from '@/features/hr/personel'
import { HR_DOCUMENT_WRITE_PERM, HR_WRITE_PERM } from '@/features/hr/permissions'
import { InstallationDetailPage } from '@/features/installations/InstallationDetailPage'
import { InstallationsPage } from '@/features/installations/InstallationsPage'
import { LicensesPage } from '@/features/licenses/LicensesPage'
import { MailManagementPage } from '@/features/mail/MailManagementPage'
import { NotificationsPage } from '@/features/notifications/NotificationsPage'
import { AssignmentRequestsPage, ModuleDetailPage, ProjectBoardsPage, ProjectCatalogPage, ProjectDetailPage, TaskDetailPage } from '@/features/projects'
import { ReleasesPage } from '@/features/releases/ReleasesPage'
import { OperationsReportPage } from '@/features/reports/pages/OperationsReportPage'
import { OverviewReportPage } from '@/features/reports/pages/OverviewReportPage'
import { ProfitabilityReportPage } from '@/features/reports/pages/ProfitabilityReportPage'
import { SalesReportPage } from '@/features/reports/pages/SalesReportPage'
import { TicketsPage } from '@/features/tickets/TicketsPage'
import { useAuth } from '@/lib/auth/AuthProvider'
import type { PermissionCheck } from '@/lib/auth/permissions'

function FullscreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-muted-foreground">
      <Loader2 className="size-6 animate-spin" />
    </div>
  )
}

function RequireAuth() {
  const { status, user } = useAuth()
  const loc = useLocation()
  if (status === 'loading') return <FullscreenLoader />
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: loc.pathname }} />
  if (user?.must_change_password && loc.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }
  return <Outlet />
}

function Guard({ perm, children }: { perm?: PermissionCheck; children: ReactNode }) {
  const { can } = useAuth()
  const { t } = useTranslation()
  return can(perm) ? <>{children}</> : <EmptyState title={t('errors.FORBIDDEN')} />
}

function RedirectAccount() {
  const { id } = useParams()
  return <Navigate to={`/customers/${id ?? ''}`} replace />
}

function RedirectKeepSearch({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

const navPerm = (key: string) => NAV.flatMap((g) => g.items).find((i) => i.key === key)?.perm

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: 'change-password', element: <ChangePasswordPage /> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'me/documents', element: <MyDocumentsPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'ana-sayfa/duzenle', element: <DashboardEditPage /> },
          { path: 'customers', element: <Guard perm={navPerm('crmOverview')}><CrmOverviewPage /></Guard> },
          { path: 'customers/list', element: <Guard perm={navPerm('crmCustomers')}><CustomersPage /></Guard> },
          { path: 'customers/followups', element: <Guard perm={navPerm('crmFollowups')}><CrmFollowupsPage /></Guard> },
          { path: 'customers/orders', element: <Guard perm={navPerm('crmOrders')}><CrmOrdersPage /></Guard> },
          { path: 'customers/renewals', element: <Guard perm={navPerm('crmRenewals')}><CrmRenewalsPage /></Guard> },
          { path: 'customers/offers', element: <Guard perm={navPerm('crmOffers')}><CrmOffersPage /></Guard> },
          { path: 'customers/opportunities', element: <Navigate to="/customers/list?view=board" replace /> },
          { path: 'customers/meetings', element: <Guard perm={navPerm('crmMeetings')}><CrmMeetingsPage /></Guard> },
          { path: 'customers/expenses', element: <Guard perm={navPerm('crmExpenses')}><CustomerExpensesPage /></Guard> },
          { path: 'customers/delivery', element: <Guard perm={navPerm('crmDelivery')}><DeliveryPage /></Guard> },
          { path: 'customers/collections', element: <Guard perm={navPerm('crmCollections')}><CollectionsPage /></Guard> },
          { path: 'customers/support', element: <Guard perm={navPerm('crmSupport')}><SupportPage /></Guard> },
          { path: 'customers/access', element: <Guard perm={navPerm('crmAccess')}><AccessRequestsPage /></Guard> },
          { path: 'customers/:id/live/:meetingId', element: <Guard perm={navPerm('crmCustomers')}><MeetingLivePage /></Guard> },
          { path: 'customers/:id/:tab?', element: <Guard perm={navPerm('crmCustomers')}><CustomerDetailPage /></Guard> },
          { path: 'finance', element: <Guard perm={navPerm('financeOverview')}><FinanceOverviewPage /></Guard> },
          { path: 'finance/accounts', element: <Guard perm={navPerm('financeAccounts')}><FinanceAccountsPage key="money" scope="money" /></Guard> },
          { path: 'finance/cards', element: <Guard perm={navPerm('financeCards')}><FinanceAccountsPage key="cards" scope="cards" /></Guard> },
          { path: 'finance/transactions', element: <Guard perm={navPerm('financeTransactions')}><TransactionsPage /></Guard> },
          { path: 'finance/obligations', element: <Guard perm={navPerm('financeObligations')}><ObligationsPage /></Guard> },
          { path: 'finance/payment-requests', element: <Guard perm={navPerm('financePaymentRequests')}><PaymentRequestsPage /></Guard> },
          { path: 'finance/deposits', element: <Guard perm={navPerm('financeDeposits')}><DepositsPage /></Guard> },
          { path: 'finance/fx', element: <Guard perm={navPerm('financeFx')}><FxPage /></Guard> },
          { path: 'finance/bank-report', element: <Guard perm={navPerm('financeBankReport')}><BankReportPage /></Guard> },
          { path: 'finance/pnl', element: <Guard perm={navPerm('financePnL')}><PnLPage /></Guard> },
          { path: 'finance/reimbursements', element: <Guard perm={navPerm('financeReimbursements')}><ReimbursementsPage /></Guard> },
          { path: 'finance/staff-advances', element: <Guard perm={navPerm('financeStaffAdvances')}><StaffAdvancesPage /></Guard> },
          { path: 'finance/budgets', element: <Guard perm={navPerm('financeBudgets')}><BudgetsPage /></Guard> },
          { path: 'finance/departments', element: <Guard perm={navPerm('financeDepartments')}><FinanceDepartmentsPage /></Guard> },
          { path: 'finance/banks', element: <Guard perm={navPerm('financeBanks')}><BanksPage /></Guard> },
          { path: 'finance/loans', element: <Guard perm={navPerm('financeLoans')}><FinanceLoansPage /></Guard> },
          { path: 'finance/cash-advances', element: <Guard perm={navPerm('financeCashAdvances')}><FinanceCashAdvancesPage /></Guard> },
          { path: 'finance/parties', element: <Guard perm={navPerm('financeParties')}><PartiesPage /></Guard> },
          { path: 'finance/personal', element: <Guard perm={navPerm('financePersonal')}><PersonalFinancePage /></Guard> },
          { path: 'finance/cash-flow', element: <Guard perm={navPerm('financeCashFlow')}><CashFlowPage /></Guard> },
          { path: 'finance/categories', element: <Guard perm={navPerm('financeCategories')}><CategoriesPage /></Guard> },
          { path: 'finance/tax', element: <RedirectKeepSearch to="/admin/tax" /> },
          { path: 'finance/books', element: <Guard perm={navPerm('financeBooks')}><LedgerBooksPage /></Guard> },
          { path: 'finance/approvals', element: <Guard perm={navPerm('financeApprovals')}><VoidRequestsPage /></Guard> },
          { path: 'finance/e-documents', element: <Guard perm={navPerm('financeEDocs')}><EDocumentsPage /></Guard> },
          { path: 'projects', element: <Navigate to="/projects/boards" replace /> },
          { path: 'projects/rules', element: <Guard perm={navPerm('projectRules')}><TaskRulesPage /></Guard> },
          { path: 'projects/tasks', element: <Guard perm={navPerm('projectTasks')}><TaskBoardPage /></Guard> },
          { path: 'projects/boards', element: <Guard perm={navPerm('projectBoards')}><ProjectBoardsPage /></Guard> },
          { path: 'projects/catalog', element: <Guard perm={navPerm('projectCatalog')}><ProjectCatalogPage /></Guard> },
          { path: 'projects/catalog/:moduleId', element: <Guard perm={navPerm('projectCatalog')}><ModuleDetailPage /></Guard> },
          { path: 'projects/assignment-requests', element: <Guard perm={navPerm('projectAssignments')}><AssignmentRequestsPage /></Guard> },
          { path: 'projects/:projectId/tasks/:taskId', element: <Guard perm={navPerm('projectBoards')}><TaskDetailPage /></Guard> },
          { path: 'projects/:projectId/:tab?', element: <Guard perm={navPerm('projectBoards')}><ProjectDetailPage /></Guard> },
          { path: 'mail', element: <Guard perm={navPerm('mailbox')}><MailManagementPage /></Guard> },
          { path: 'hr/personel', element: <Guard perm={navPerm('hrPersonel')}><PersonelListPage /></Guard> },
          { path: 'hr/personel/yeni', element: <Guard perm={HR_WRITE_PERM}><PersonelCreatePage /></Guard> },
          { path: 'hr/personel/:id/duzenle', element: <Guard perm={HR_WRITE_PERM}><PersonelEditPage /></Guard> },
          { path: 'hr/personel/:id', element: <Guard perm={navPerm('hrPersonel')}><PersonelDetailPage /></Guard> },
          { path: 'hr/belgeler', element: <Guard perm={navPerm('hrBelgeler')}><BelgelerListPage /></Guard> },
          { path: 'hr/belgeler/takip', element: <Guard perm={navPerm('hrBelgeTakip')}><BelgeTakipPage /></Guard> },
          { path: 'hr/belgeler/sablonlar/yeni', element: <Guard perm={HR_DOCUMENT_WRITE_PERM}><SablonOlusturPage /></Guard> },
          { path: 'hr/belgeler/sablonlar/:id/duzenle', element: <Guard perm={HR_DOCUMENT_WRITE_PERM}><SablonDuzenleyiciPage /></Guard> },
          { path: 'hr/belgeler/sablonlar/:id/surum', element: <Guard perm={HR_DOCUMENT_WRITE_PERM}><SablonSurumPage /></Guard> },
          { path: 'hr/bordro', element: <Guard perm={navPerm('hrBordro')}><BordroPage /></Guard> },
          { path: 'hr/odemeler', element: <Guard perm={navPerm('hrOdemeler')}><OdemelerPage /></Guard> },
          { path: 'hr/departmanlar', element: <Guard perm={navPerm('departments')}><DepartmentsPage /></Guard> },
          { path: 'hr/pozisyonlar', element: <Guard perm={navPerm('hrPositions')}><PositionsPage /></Guard> },
          { path: 'accounts', element: <Navigate to="/customers/list" replace /> },
          { path: 'accounts/:id', element: <RedirectAccount /> },
          { path: 'meetings', element: <Navigate to="/customers/meetings" replace /> },
          { path: 'followups', element: <Navigate to="/customers/followups" replace /> },
          { path: 'orders', element: <Navigate to="/customers/orders" replace /> },
          { path: 'renewals', element: <Navigate to="/customers/renewals" replace /> },
          { path: 'tasks', element: <Navigate to="/projects/tasks" replace /> },
          { path: 'demos', element: <Guard perm="accounts.read"><DemosPage /></Guard> },
          { path: 'tickets', element: <Guard perm="tickets.write"><TicketsPage /></Guard> },
          { path: 'installations', element: <Guard perm="accounts.read"><InstallationsPage /></Guard> },
          { path: 'installations/:id', element: <Guard perm="accounts.read"><InstallationDetailPage /></Guard> },
          { path: 'licenses', element: <Guard perm="licenses.issue"><LicensesPage /></Guard> },
          { path: 'releases', element: <Guard perm="releases.write"><ReleasesPage /></Guard> },
          { path: 'reports', element: <Guard perm={navPerm('reportOverview')}><OverviewReportPage /></Guard> },
          { path: 'reports/sales', element: <Guard perm={navPerm('reportSales')}><SalesReportPage /></Guard> },
          { path: 'reports/profitability', element: <Guard perm={navPerm('reportProfitability')}><ProfitabilityReportPage /></Guard> },
          { path: 'reports/finance', element: <Navigate to="/finance" replace /> },
          { path: 'reports/customers', element: <Navigate to="/customers" replace /> },
          { path: 'reports/operations', element: <Guard perm={navPerm('reportOperations')}><OperationsReportPage /></Guard> },
          { path: 'reports/hr', element: <Navigate to="/hr/personel" replace /> },
          { path: 'admin/users', element: <Navigate to="/access/users" replace /> },
          { path: 'admin/tax', element: <Guard perm={navPerm('tax')}><TaxPage /></Guard> },
          { path: 'admin/catalog', element: <Navigate to="/projects/catalog?tab=packages" replace /> },
          { path: 'admin/pipelines', element: <Guard perm={navPerm('pipelines')}><PipelinesPage /></Guard> },
          { path: 'admin/task-templates', element: <Navigate to="/projects/rules" replace /> },
          { path: 'admin/audit', element: <Guard perm={navPerm('audit')}><AuditPage /></Guard> },
          { path: 'admin/infra', element: <Guard perm={navPerm('infra')}><InfraPage /></Guard> },
          { path: 'admin/infra/servers/:id', element: <Guard perm={navPerm('infra')}><InfraServerDetailPage /></Guard> },
          { path: 'admin/hr', element: <Navigate to="/hr/personel" replace /> },
          { path: 'admin/mail', element: <Guard perm={navPerm('mail')}><MailAccountsPage /></Guard> },
          { path: 'admin/regions', element: <Guard perm={navPerm('regions')}><RegionsPage /></Guard> },
          { path: 'admin/customer-categories', element: <Guard perm={navPerm('customerCategories')}><CustomerCategoriesPage /></Guard> },
          { path: 'admin/discounts', element: <Guard perm={navPerm('discounts')}><DiscountLimitsPage /></Guard> },
          { path: 'admin/website', element: <Navigate to="/admin/pipelines" replace /> },
          { path: 'access/users', element: <Guard perm={navPerm('accessUsers')}><UsersPage /></Guard> },
          { path: 'access/roles', element: <Guard perm={navPerm('accessRoles')}><RolesPage /></Guard> },
          { path: 'access/roles/:id', element: <Guard perm={navPerm('accessRoles')}><RoleDetailPage /></Guard> },
          { path: '*', element: <EmptyState /> },
        ],
      },
    ],
  },
])
